import { useState, useCallback } from 'react';
import { ToastSeverity } from '../components/Toast';

export interface ToastState {
    open: boolean;
    message: string;
    title?: string;
    severity: ToastSeverity;
}

export interface UseToastReturn {
    toast: ToastState;
    showToast: (message: string, severity?: ToastSeverity, title?: string) => void;
    showSuccess: (message: string, title?: string) => void;
    showError: (message: string, title?: string) => void;
    showWarning: (message: string, title?: string) => void;
    showInfo: (message: string, title?: string) => void;
    hideToast: () => void;
}

const useToast = (): UseToastReturn => {
    const [toast, setToast] = useState<ToastState>({
        open: false,
        message: '',
        title: undefined,
        severity: 'success'
    });

    const showToast = useCallback((message: string, severity: ToastSeverity = 'success', title?: string) => {
        setToast({
            open: true,
            message,
            title,
            severity
        });
    }, []);

    const showSuccess = useCallback((message: string, title?: string) => {
        showToast(message, 'success', title);
    }, [showToast]);

    const showError = useCallback((message: string, title?: string) => {
        showToast(message, 'error', title);
    }, [showToast]);

    const showWarning = useCallback((message: string, title?: string) => {
        showToast(message, 'warning', title);
    }, [showToast]);

    const showInfo = useCallback((message: string, title?: string) => {
        showToast(message, 'info', title);
    }, [showToast]);

    const hideToast = useCallback(() => {
        setToast(prev => ({ ...prev, open: false }));
    }, []);

    return {
        toast,
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        hideToast
    };
};

export default useToast;
